import type { CroppedAreaPixels } from "../types";

/**
 * Validates and normalizes crop coordinates to ensure they're within image bounds.
 */
function validateAndNormalizeCrop(
  croppedAreaPixels: CroppedAreaPixels,
  imageWidth: number,
  imageHeight: number
): { x: number; y: number; width: number; height: number } | null {
  const cropX = Math.max(0, Math.min(croppedAreaPixels.x, imageWidth));
  const cropY = Math.max(0, Math.min(croppedAreaPixels.y, imageHeight));
  const cropWidth = Math.min(croppedAreaPixels.width, imageWidth - cropX);
  const cropHeight = Math.min(croppedAreaPixels.height, imageHeight - cropY);

  if (cropWidth <= 0 || cropHeight <= 0) {
    return null;
  }

  return { x: cropX, y: cropY, width: cropWidth, height: cropHeight };
}

/**
 * Draws the cropped and rotated image onto the canvas.
 */
function drawCroppedImage(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  crop: { x: number; y: number; width: number; height: number },
  rotation: number
): void {
  // Apply rotation if needed
  if (rotation !== 0) {
    ctx.translate(crop.width / 2, crop.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-crop.width / 2, -crop.height / 2);
  }

  // Draw cropped image from source to canvas
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
}

/**
 * Processes the loaded image and creates a cropped blob.
 */
function processImage(
  image: HTMLImageElement,
  croppedAreaPixels: CroppedAreaPixels,
  rotation: number,
  resolve: (blob: Blob) => void,
  reject: (error: Error) => void
): void {
  // Ensure image is fully decoded
  if (image.naturalWidth === 0 || image.naturalHeight === 0) {
    reject(new Error("Image has invalid dimensions"));
    return;
  }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    reject(new Error("Could not get canvas context"));
    return;
  }

  // Validate and normalize crop coordinates
  const crop = validateAndNormalizeCrop(croppedAreaPixels, image.naturalWidth, image.naturalHeight);

  if (!crop) {
    reject(new Error("Invalid crop dimensions"));
    return;
  }

  // Set canvas size to cropped area
  canvas.width = crop.width;
  canvas.height = crop.height;

  // Draw cropped and rotated image
  drawCroppedImage(ctx, image, crop, rotation);

  // Convert canvas to blob
  canvas.toBlob(
    (blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Failed to create blob"));
      }
    },
    "image/jpeg",
    0.85
  );
}

/**
 * Creates a cropped image from source image using Canvas API.
 *
 * @param imageSrc - Base64 or URL string of the source image
 * @param croppedAreaPixels - Pixel coordinates defining the crop area
 * @param rotation - Rotation angle in degrees (0-360)
 * @returns Promise resolving to JPEG blob with 85% quality compression
 *
 * @example
 * ```tsx
 * const croppedBlob = await createCroppedImage(
 *   imageDataUrl,
 *   { x: 100, y: 100, width: 400, height: 400 },
 *   45
 * );
 * ```
 */
export async function createCroppedImage(
  imageSrc: string,
  croppedAreaPixels: CroppedAreaPixels,
  rotation: number = 0
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image();

    // Set error handler FIRST
    image.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    // Set load handler BEFORE setting src
    image.onload = () => {
      try {
        processImage(image, croppedAreaPixels, rotation, resolve, reject);
      } catch (error) {
        reject(error);
      }
    };

    // Only set crossOrigin for non-data URLs (external images)
    if (!imageSrc.startsWith("data:")) {
      image.crossOrigin = "anonymous";
    }

    // Set src LAST to trigger loading
    image.src = imageSrc;
  });
}
